var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as Cookies from 'js-cookie';
import decode from 'jwt-decode';
import { UserType } from '@lendi/core-constants';
import { ErrorCodes, GenericError, getAudience, getClientID, getDomain, getStateFromAuthResult, getStateFromStorage, getURLs, setReturnURLInStorage, setStateInStorage, Type, getTypeFromHostname, LalaSentry, SAFETY_NET_COOKIE_NAME, LG_APP_AUTH_SESSION_COOKIE_NAME, } from '@lendi/lala-utils';
import { WebAuth } from 'auth0-js';
import { BroadcastChannel } from 'broadcast-channel';
import { TinyEmitter } from 'tiny-emitter';
import { v4 as uuidv4 } from 'uuid';
import { getUserFromAPI } from './getUserFromAPI';
import { saveMeToStorage } from './MeSessionStorage';
import { Status, } from './types';
var EXPIRED_WITHIN_LIMIT = 120000; // 2 minutes
function isAuthenticatedState(state) {
    // tslint:disable-next-line: no-unnecessary-type-assertion
    return state.token !== undefined;
}
function isUnauthenticatedState(state) {
    // tslint:disable-next-line: no-unnecessary-type-assertion
    return state === undefined;
}
/**
 * Session defines and instantiates the connection with our authentication service
 *
 * @export
 * @class Session
 */
var Session = /** @class */ (function () {
    /**
     * Creates an instance of Session.
     * @param {SessionOptions} options
     * @memberof Session
     */
    function Session(options) {
        this._emitter = new TinyEmitter();
        /**
         * If the environment is test then we use simulate type so that our tests do not break
         * as referenced here: https://github.com/pubkey/broadcast-channel/issues/8
         * using simulate is documented here: https://github.com/pubkey/broadcast-channel#methods
         */
        this._lalaChannel = new BroadcastChannel('lala', __assign({}, (process.env.NODE_ENV === 'test' && { type: 'simulate' })));
        this.responseType = 'token id_token';
        this.uuidregex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        var environment = options.environment, brand = options.brand, type = options.type, scope = options.scope, audience = options.audience, forceRefresh = options.forceRefresh, lendiGroupTeamView = options.lendiGroupTeamView;
        // tslint:disable-next-line no-console
        console.log('Construct session', {
            environment: environment,
            brand: brand,
            type: type,
            scope: scope,
            audience: audience,
            forceRefresh: forceRefresh,
            lendiGroupTeamView: lendiGroupTeamView,
            logV: '1.7',
        });
        this.environment = environment;
        this.type = type;
        this.brand = brand;
        this.lendiGroupTeamView = lendiGroupTeamView;
        var domain = getDomain(this.brand, this.type, this.environment);
        var clientID = getClientID(this.brand, this.type, this.environment);
        var apiAudience = audience || getAudience(environment);
        this.auth0 = new WebAuth({
            domain: domain,
            clientID: clientID,
            scope: scope,
            audience: apiAudience,
            responseType: this.responseType,
            redirectUri: getURLs(this.brand, this.type, this.environment, lendiGroupTeamView).callback,
        });
        var state = getStateFromStorage();
        /* tslint:disable:no-floating-promises */
        this.init(state, forceRefresh)
            .then(function () { return LalaSentry.logToSentry('authentication successful'); })
            .catch(function (err) { return LalaSentry.captureException(err); });
    }
    Session.prototype.on = function (event, listener) {
        this._emitter.on(event, listener);
        return this;
    };
    Session.prototype.off = function (event, listener) {
        this._emitter.off(event, listener);
        return this;
    };
    Session.prototype.handleLalaBroadcastMessage = function (state) {
        setStateInStorage(state, false);
        this.update(state);
    };
    Session.prototype.listenToBroadcastChannel = function () {
        this._lalaChannel.addEventListener('message', this.handleLalaBroadcastMessage.bind(this));
    };
    Session.prototype.stopListeningToBroadcastChannel = function () {
        this._lalaChannel.removeEventListener('message', this.handleLalaBroadcastMessage.bind(this));
        // tslint:disable-next-line: no-floating-promises
        this._lalaChannel.close();
    };
    Object.defineProperty(Session.prototype, "status", {
        /**
         * The status of the session
         */
        get: function () {
            return this._status || Status.Authenticating;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "expiry", {
        /**
         * If there is a time when the session is going to expire, it is returned
         */
        get: function () {
            return this._expiry;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "token", {
        /**
         * The access token for the session
         */
        get: function () {
            return this._token;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "identity", {
        /**
         * Details about the identity authenticated by this session
         */
        get: function () {
            return this._identity;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "error", {
        get: function () {
            return this._error;
        },
        enumerable: false,
        configurable: true
    });
    Session.prototype.reset = function () {
        this._expiry = undefined;
        this._token = undefined;
        this._identity = undefined;
        this._error = undefined;
        Cookies.set(LG_APP_AUTH_SESSION_COOKIE_NAME, '', { expires: new Date(Date.now() - 1) });
        LalaSentry.logInfo('Reset done.');
    };
    Session.prototype.login = function (state) {
        var _this = this;
        this.update(state);
        if (this.token && isAuthenticatedState(state)) {
            getUserFromAPI(this.environment, this.token)
                .then(function (user) {
                saveMeToStorage(__assign(__assign({}, user), { phone: user.mobileNumber }));
            })
                .catch(function (e) {
                var _a;
                LalaSentry.logError('login getUserFromAPI failed', {
                    userId: (_a = state === null || state === void 0 ? void 0 : state.identity) === null || _a === void 0 ? void 0 : _a.id,
                    type: _this.type,
                    error: e,
                });
                // Swallow error, the application will
                // still run fine without this code.
            });
        }
    };
    // An extra step on initialisation is to check whether Auth0 considers the customer authenticated
    // this means that even if you change tab and we lose your sessionStorage we will still consider you authenticated
    Session.prototype.init = function (state, forceRefresh) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        LalaSentry.logInfo('Initializing session...', { userId: (_a = state === null || state === void 0 ? void 0 : state.identity) === null || _a === void 0 ? void 0 : _a.id, type: this.type });
                        if (!(isUnauthenticatedState(state) && this.type !== Type.Team)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.refresh(undefined, undefined, forceRefresh)];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        this.update(state);
                        _b.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    Session.prototype.update = function (state) {
        var _this = this;
        var _a;
        LalaSentry.logInfo('Updating session...', { userId: (_a = state === null || state === void 0 ? void 0 : state.identity) === null || _a === void 0 ? void 0 : _a.id });
        // This checks whether we are running this function on the backend and will set status as authenticating
        // This allows control to frontends to what should be shown irrespective of authentication status
        if (typeof window === 'undefined') {
            this.reset();
            this._status = Status.Authenticating;
            this._emitter.emit('session', {});
        }
        else if (isUnauthenticatedState(state)) {
            this.reset();
            this._status = Status.Unauthenticated;
            this._emitter.emit('session', {});
        }
        else if (isAuthenticatedState(state)) {
            var token = state.token, expiry = state.expiry, identity = state.identity;
            this._token = token;
            this._identity = identity;
            this._expiry = expiry;
            this._error = undefined;
            if (expiry <= Date.now()) {
                // checks if user expired recently within 2 mins, else we will call it out `unAuthenticated`
                if (expiry >= Date.now() - EXPIRED_WITHIN_LIMIT) {
                    this._status = Status.Expired;
                    this._emitter.emit('expire', {});
                }
                else {
                    this._status = Status.Unauthenticated;
                }
                Cookies.set(LG_APP_AUTH_SESSION_COOKIE_NAME, '', { expires: new Date(Date.now() - 1) });
            }
            else {
                this._status = Status.Authenticated;
                this._emitter.emit('session', {});
                var sessionId = uuidv4();
                var id = (identity !== null && identity !== void 0 ? identity : {}).id;
                var defaultExpiryMilliSeconds = 8 * 60 * 60 * 1000;
                var expires = new Date((expiry || Date.now() + defaultExpiryMilliSeconds) - 1);
                Cookies.set(LG_APP_AUTH_SESSION_COOKIE_NAME, "".concat(sessionId, ",").concat(id), { expires: expires });
                if (expiry) {
                    window.clearTimeout(this.expiryTimeout);
                    this.expiryTimeout = window.setTimeout(function () {
                        _this._status = Status.Expired;
                        _this._emitter.emit('expire', {});
                        Cookies.set(LG_APP_AUTH_SESSION_COOKIE_NAME, '', { expires: new Date(Date.now() - 1) });
                    }, expiry - Date.now());
                }
            }
        }
        else {
            var error = state.error;
            this._status = Status.Errored;
            this._token = undefined;
            this._identity = undefined;
            this._expiry = undefined;
            this._error = error;
            this._emitter.emit('error', {});
        }
        LalaSentry.logInfo('Session updated', { status: this._status });
    };
    Session.prototype.clearExpiry = function () {
        LalaSentry.logInfo('Clearing expiry', { status: this._status });
        if (this._status === Status.Expired) {
            this._status = Status.Unauthenticated;
            this._emitter.emit('session', {});
        }
    };
    Session.prototype.getTokenValues = function (applicationId) {
        // typeof window === 'undefined' -> ssr support
        var pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        var maybeapplicatinoid = pathname.split('/')[5];
        var applicationIdFromUrl = maybeapplicatinoid && this.uuidregex.test(maybeapplicatinoid) ? maybeapplicatinoid : undefined;
        /**
         * If there is a token,
         * then we need to stick to the values to token has defined,
         * no matter what the url says
         *
         * the url only serves for the fallback case - that is, in case, we don't have obtained a token yet
         */
        if (this.token) {
            var jwt = decode(this.token);
            return {
                customer: jwt['authorized-customer'],
                // if we got a param, we want exactly that application
                // else, if we are in TV already, make sure we get - at least - the current application back
                application: applicationId !== null && applicationId !== void 0 ? applicationId : applicationIdFromUrl,
                // even if we were in TV with a customer or M2M token, we'd never ask for a specific application or customer
                isTeamView: jwt['https://lendi/type'] === UserType.Team,
            };
        }
        // we are not in a refresh scenario yet, this is the first run
        return {
            customer: pathname.split('/')[3],
            // for the first-time flow, we never have a manual overwrite, we always get this from the url
            application: applicationIdFromUrl,
            isTeamView: getTypeFromHostname() === Type.Team,
        };
    };
    Session.prototype.refresh = function (audience, demandApplicationID, forceRefresh) {
        if (audience === void 0) { audience = getAudience(this.environment); }
        return __awaiter(this, void 0, void 0, function () {
            var state, _a, customerID, applicationID, isTeamView, sessionCookieValue, randomUUID, newState;
            var _this = this;
            return __generator(this, function (_b) {
                LalaSentry.logInfo('Refreshing session...', { status: this._status });
                // auth0-js doesn't support any SSR and will cause the auth0.checkSession to break in the nextJS apps before it's mounted
                if (typeof window === 'undefined') {
                    this.reset();
                    this._status = Status.Authenticating;
                    this._emitter.emit('session', {});
                    return [2 /*return*/, Promise.reject()];
                }
                // if we don't have a status then set the state to Authenticating while we are awaiting check session
                if (this._status === undefined) {
                    this._status = Status.Authenticating;
                }
                _a = this.getTokenValues(demandApplicationID), customerID = _a.customer, applicationID = _a.application, isTeamView = _a.isTeamView;
                sessionCookieValue = Cookies.get(LG_APP_AUTH_SESSION_COOKIE_NAME);
                LalaSentry.logInfo('Rejection check', { forceRefresh: forceRefresh, sessionCookieValue: sessionCookieValue, isTeamView: isTeamView });
                if (!forceRefresh && !sessionCookieValue && !isTeamView) {
                    setStateInStorage(undefined);
                    this.update(undefined);
                    return [2 /*return*/, Promise.reject("Refresh cannot proceed! ".concat(forceRefresh, ", ").concat(sessionCookieValue))];
                }
                if (isTeamView && customerID) {
                    randomUUID = uuidv4();
                    newState = customerID + '@@@' + randomUUID + (applicationID ? '$$$' + applicationID : '');
                    state = btoa(newState);
                }
                LalaSentry.logInfo('Calling checkSession...', { status: this._status });
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        _this.auth0.checkSession({ audience: audience, state: state }, function (error, result) {
                            if (error && error.code === 'login_required') {
                                setStateInStorage(undefined);
                                _this.update(undefined);
                                reject();
                            }
                            else {
                                var state_1 = getStateFromAuthResult(error || undefined, result || undefined);
                                setStateInStorage(state_1);
                                _this.login(state_1);
                                resolve(_this.token);
                            }
                        });
                    })];
            });
        });
    };
    Session.prototype.clearSession = function () {
        localStorage.clear();
        sessionStorage.clear();
        Cookies.set(SAFETY_NET_COOKIE_NAME, 'false');
    };
    Session.prototype.logout = function () {
        var returnURL = getURLs(this.brand, this.type, this.environment).unauthenticated;
        this.auth0.logout({
            returnTo: returnURL,
        });
        this._status = Status.Unauthenticating;
        this.reset();
        this.clearSession();
        // Added setStateInStorage with false for broadcasting to keep existing functionality
        // But I guess it needs to be changed to true to log out the user from other tabs as well.
        setStateInStorage(undefined, false);
        // notify listeners that the session has ended
        this._emitter.emit('logout', {});
    };
    Session.prototype.DEPRECATED_RESET = function () {
        setStateInStorage(undefined);
        this.reset();
        this._emitter.emit('logout', {});
    };
    /**
     * @private
     * Avoid directly calling this method in a application that isn't a login page because there
     * may be some slight changes if the auth implementation changes e.g. Auth0 => Cognito
     */
    Session.prototype.sendCode = function (phoneNumber) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        _this.auth0.passwordlessStart({
                            connection: 'sms',
                            send: 'code',
                            phoneNumber: phoneNumber,
                        }, function (err) {
                            if (err) {
                                // tslint:disable-next-line: no-unnecessary-type-assertion
                                var error = getStateFromAuthResult(err, undefined).error;
                                reject(error);
                                return;
                            }
                            resolve();
                        });
                    })];
            });
        });
    };
    /**
     * @private
     * Avoid directly calling this method in a application that isn't a login page because there
     * may be some slight changes if the auth implementation changes e.g. Auth0 => Cognito
     */
    Session.prototype.verifyCode = function (_a) {
        var phone = _a.phone, code = _a.code, email = _a.email, leadId = _a.leadId, firstName = _a.firstName;
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_b) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        var callback = function (error, result) {
                            LalaSentry.logInfo('VerifyCode callback', { error: error });
                            var state = getStateFromAuthResult(error || undefined, result || undefined);
                            setStateInStorage(state);
                            _this.login(state);
                            if (state) {
                                if (isAuthenticatedState(state)) {
                                    resolve({ valid: true, token: state.token });
                                }
                                else if (isUnauthenticatedState(state)) {
                                    // unreachable code, getStateFromAuthResult never returns undefined
                                    LalaSentry.logInfo('isUnauthenticatedState is true!');
                                    resolve({ valid: true });
                                }
                                else {
                                    reject(state.error);
                                }
                            }
                            else {
                                // unreachable code, getStateFromAuthResult never returns undefined
                                LalaSentry.logInfo('undefined state!');
                                resolve({ valid: false });
                            }
                        };
                        var state = btoa(email + '@@@' + (leadId ? leadId + '###' : '') + (firstName ? firstName + '$$$' : '') + uuidv4());
                        LalaSentry.logInfo('VerifyCode');
                        /*
                          loginWithCredentials() instead of passwordlessLogin() is a hack from
                          Auth0 to prevent the user being redirected
                        */
                        _this.auth0.popup.loginWithCredentials({
                            connection: 'sms',
                            username: phone,
                            password: code,
                            state: state,
                        }, callback);
                    })];
            });
        });
    };
    /**
     * @private
     * Avoid directly calling this method in a application that isn't a login page because there
     * may be some slight changes if the auth implementation changes e.g. Auth0 => Cognito
     */
    Session.prototype.popupAuthorise = function (_a) {
        var _this = this;
        var connection = _a.connection, audience = _a.audience, scope = _a.scope, returnURL = _a.returnURL, state = _a.state, loginHint = _a.loginHint;
        return new Promise(function (resolve, reject) {
            var callback = function (error, result) {
                var state = getStateFromAuthResult(error || undefined, result || undefined);
                // auth0 returns an error when user closes google popup window and we want to leave state unchanged
                if (error) {
                    var errorState = state;
                    if (errorState.error.code === ErrorCodes.POPUP_CLOSED) {
                        LalaSentry.logError('SSO failed!', { errorCode: errorState.error.code, error: errorState.error });
                        reject(errorState.error);
                        return;
                    }
                }
                LalaSentry.logInfo('SSO auth passed.');
                setStateInStorage(state);
                _this.login(state);
                if (state) {
                    if (isAuthenticatedState(state)) {
                        resolve({ existingCustomer: state.identity.existingUser, token: state.token });
                    }
                    else if (isUnauthenticatedState(state)) {
                        resolve(undefined);
                    }
                    else {
                        reject(state.error);
                    }
                }
                else {
                    reject(new GenericError());
                }
            };
            if (returnURL) {
                setReturnURLInStorage(returnURL);
            }
            _this.auth0.popup.authorize(__assign({ redirectUri: "".concat(getURLs(_this.brand, _this.type, _this.environment).popupCallback), connection: connection, audience: audience || getAudience(_this.environment), scope: scope, state: state, prompt: 'select_account', responseType: 'token id_token' }, (loginHint && { login_hint: loginHint })), callback);
        });
    };
    /**
     * @private
     * Avoid directly calling this method in a application that isn't a login page because there
     * may be some slight changes if the auth implementation changes e.g. Auth0 => Cognito
     */
    Session.prototype.popupAuthoriseWithoutSetSession = function (_a) {
        var _this = this;
        var connection = _a.connection, audience = _a.audience, scope = _a.scope, state = _a.state;
        return new Promise(function (resolve, reject) {
            var callback = function (error, result) {
                LalaSentry.logInfo('SSO auth done without set session.', { error: error });
                if (result) {
                    var accessToken = result.accessToken, idTokenPayload = result.idTokenPayload;
                    resolve({ token: accessToken, idTokenPayload: idTokenPayload });
                }
                else {
                    reject(new GenericError());
                }
            };
            _this.auth0.popup.authorize({
                redirectUri: "".concat(getURLs(_this.brand, _this.type, _this.environment).popupCallback),
                connection: connection,
                audience: audience || getAudience(_this.environment),
                scope: scope,
                state: state,
                responseType: _this.responseType,
            }, callback);
        });
    };
    /**
     * @private
     * Avoid directly calling this method in a application that isn't a login page because there
     * may be some slight changes if the auth implementation changes e.g. Auth0 => Cognito
     */
    Session.prototype.authorise = function (_a) {
        var connection = _a.connection, audience = _a.audience, scope = _a.scope, returnURL = _a.returnURL, state = _a.state;
        LalaSentry.logInfo('authorise called.', { returnURL: returnURL });
        if (returnURL) {
            setReturnURLInStorage(returnURL);
        }
        this.auth0.authorize({
            connection: connection,
            audience: audience || getAudience(this.environment),
            scope: scope,
            state: state,
        });
    };
    return Session;
}());
export { Session };
//# sourceMappingURL=Session.js.map